import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Stethoscope, ChevronDown } from "lucide-react";
import keycloak from "../APIs/keycloak";
import { useCurrentUser } from "../APIs/UserContext";

const FEEDBACK_PATIENT_URL =
  "https://docs.google.com/forms/d/1sLnmGfgKje3MGF0j15M0U1AZcXZqm-7My_OV-QUXSlk";
const FEEDBACK_PROVIDER_URL =
  "https://docs.google.com/forms/d/1qsFtEfoNHC_lhYgKOjhjIrAJZ5x2rxgpFCtw8zyUKQY";

const DEMO_VIDEO_URL = "https://youtu.be/ETgrzRfi0R8";

export default function Header({ onOpenSignUp, onMatchWithProvider }) {
  const { currentUser } = useCurrentUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const feedbackRef = useRef();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const isAuthed = !!currentUser;
  const isPatient = currentUser?.userType === "PATIENT";
  const isProvider = currentUser?.userType === "PROVIDER";
  const isClaimedProvider = isProvider && !!currentUser?.providerId;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
      if (feedbackRef.current && !feedbackRef.current.contains(e.target))
        setFeedbackOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogin = () => {
    keycloak.login({
      redirectUri: window.location.origin + "/ProviderSearchPage",
    });
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    localStorage.removeItem("medmaze_signup_role_locked");
    localStorage.removeItem("medmaze_pending_claim_npi");
    keycloak.logout({
      redirectUri: window.location.origin + "/ProviderSearchPage",
    });
  };

  const handleMatchWithProvider = (e) => {
    e.preventDefault();
    if (onMatchWithProvider) {
      onMatchWithProvider(e);
    } else {
      navigate("/features/matching");
    }
  };

  return (
    <header className="w-full border-b bg-white">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center">
        {/* Logo + tagline */}
        <Link to="/" className="flex flex-col leading-tight shrink-0">
          <div className="flex items-center gap-2">
            <Stethoscope className="text-teal-600" size={28} />
            <span className="text-2xl font-bold text-gray-900">MedMaze</span>
          </div>
          <span className="text-xs text-gray-400 ml-9">
            Referral Orchestration Engine
          </span>
        </Link>

        {/* Nav + Right side — hidden on mobile */}
        <div className="ml-auto hidden md:flex items-center gap-8">
          {/* Nav links */}
          <nav className="flex items-center gap-6">
            <Link
              to="/ProviderSearchPage"
              className="text-gray-700 hover:text-teal-600 font-medium text-sm transition-colors"
            >
              Find Providers
            </Link>
            <Link
              to="/"
              className="text-gray-700 hover:text-teal-600 font-medium text-sm transition-colors"
            >
              Learn More
            </Link>
            <Link
              to="/features/referral"
              className="text-gray-700 hover:text-teal-600 font-medium text-sm transition-colors"
            >
              Referral Coordination
            </Link>
            <button
              type="button"
              onClick={handleMatchWithProvider}
              className="text-gray-700 hover:text-teal-600 font-medium text-sm transition-colors"
            >
              Match with Provider
            </button>
            {/* Give Feedback dropdown */}
            <div className="relative" ref={feedbackRef}>
              <button
                type="button"
                onClick={() => setFeedbackOpen((v) => !v)}
                className="flex items-center gap-1 text-gray-700 hover:text-teal-600 font-medium text-sm transition-colors"
              >
                Give Feedback <ChevronDown size={14} />
              </button>
              {feedbackOpen && (
                <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <a
                    href={FEEDBACK_PROVIDER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-t-lg"
                    onClick={() => setFeedbackOpen(false)}
                  >
                    Give Feedback as a Provider
                  </a>
                  <a
                    href={FEEDBACK_PATIENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-b-lg border-t border-gray-100"
                    onClick={() => setFeedbackOpen(false)}
                  >
                    Give Feedback as a Patient
                  </a>
                </div>
              )}
            </div>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Demo — always visible */}
            <button
              type="button"
              onClick={() =>
                window.open(DEMO_VIDEO_URL, "_blank", "noopener,noreferrer")
              }
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-4 py-2 text-sm transition-colors"
            >
              Demo
            </button>

            {/* Guest or unclaimed provider */}
            {(!isAuthed || (isProvider && !currentUser?.providerId)) && (
              <>
                <button
                  type="button"
                  onClick={onOpenSignUp}
                  className="text-blue-600 font-semibold hover:text-blue-700 text-sm"
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  onClick={handleLogin}
                  className="text-blue-600 font-semibold hover:text-blue-700 text-sm"
                >
                  Login
                </button>
              </>
            )}

            {/* Patient: email dropdown */}
            {isAuthed && isPatient && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((v) => !v)}
                  className="font-semibold text-gray-700 text-sm"
                >
                  {currentUser?.email || "Account"}
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow-lg z-50">
                    <Link
                      to="/PatientProfile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/ProviderSearchPage"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Home
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Provider: logout */}
            {isAuthed && isClaimedProvider && (
              <button
                type="button"
                onClick={handleLogout}
                className="text-red-600 font-semibold hover:underline text-sm"
              >
                Logout
              </button>
            )}
          </div>
        </div>

        {/* Hamburger button — mobile only */}
        <button
          type="button"
          className="ml-auto md:hidden p-2 text-gray-700 hover:text-teal-600 transition-colors"
          onClick={() => setMobileMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile nav dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-white shadow-md">
          <nav className="flex flex-col px-6 py-2 divide-y divide-gray-100">
            <Link
              to="/ProviderSearchPage"
              className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Find Providers
            </Link>
            <Link
              to="/"
              className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Learn More
            </Link>
            <Link
              to="/features/referral"
              className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Referral Coordination
            </Link>
            <button
              type="button"
              onClick={(e) => {
                handleMatchWithProvider(e);
                setMobileMenuOpen(false);
              }}
              className="py-3 text-left text-gray-700 hover:text-teal-600 font-medium text-sm"
            >
              Match with Provider
            </button>
            <a
              href={FEEDBACK_PROVIDER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Give Feedback as a Provider
            </a>
            <a
              href={FEEDBACK_PATIENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Give Feedback as a Patient
            </a>

            {/* Demo — always */}
            <div className="py-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  window.open(DEMO_VIDEO_URL, "_blank", "noopener,noreferrer");
                  setMobileMenuOpen(false);
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-4 py-2 text-sm transition-colors"
              >
                Demo
              </button>
            </div>

            {/* Guest or unclaimed provider */}
            {(!isAuthed || (isProvider && !currentUser?.providerId)) && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onOpenSignUp();
                    setMobileMenuOpen(false);
                  }}
                  className="py-3 text-blue-600 font-semibold hover:text-blue-700 text-sm text-left"
                >
                  Sign Up
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleLogin();
                    setMobileMenuOpen(false);
                  }}
                  className="py-3 text-blue-600 font-semibold hover:text-blue-700 text-sm text-left"
                >
                  Login
                </button>
              </>
            )}

            {/* Patient: Profile, Home, Logout */}
            {isAuthed && isPatient && (
              <>
                <Link
                  to="/PatientProfile"
                  className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                <Link
                  to="/ProviderSearchPage"
                  className="py-3 text-gray-700 hover:text-teal-600 font-medium text-sm"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="py-3 text-left text-red-600 font-semibold hover:underline text-sm"
                >
                  Logout
                </button>
              </>
            )}

            {/* Claimed provider: Logout */}
            {isAuthed && isClaimedProvider && (
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="py-3 text-left text-red-600 font-semibold hover:underline text-sm"
              >
                Logout
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
